import {Map, List, Record} from 'immutable';
import i18next from 'i18next';

import {roundToDecimals} from 'web-app/util/math';
import {type Billing, type Discount, type PlanPart} from 'web-app/react/entities/plans/model';
import {SessionBooking, type MonthlyPrice} from 'web-app/react/entities/common/session-booking';
import {type ProductBooking} from 'web-app/react/entities/common/product-booking';

import {PricingType} from '../common/pricing';
import Session from '../sessions/model';
import {getWeekdayTitle} from './helpers';
import {FREQUENCY} from './constants';

export const BILLING_ACTUAL_SESSIONS = 'ACTUAL_SESSIONS';
export const BILLING_ACTUALS_V2 = 'ACTUALS_V2';
export const BILLING_ANNUALIZED = 'ANNUALIZED';
export const BILLING_ANNUALIZED_MID_MONTH = 'ANNUALIZED_MID_MONTH';
export const BILLING_MONTHLY = 'MONTHLY';

export const DISCOUNT_ORIGIN_CUSTOM = 'custom';
export const DISCOUNT_ORIGIN_PRESET = 'preset';
export const DISCOUNT_ORIGIN_CUSTOM_BY_PERCENTAGE = 'custom-by-percentage';
export const DISCOUNT_ORIGIN_CUSTOM_BY_AMOUNT = 'custom-by-amount';

export const billingIsActualsV1 = (billing: Billing) => billing && billing.id === BILLING_ACTUAL_SESSIONS;
export const billingIsActualsV2 = (billing: Billing) => billing && billing.id === BILLING_ACTUALS_V2;

export const billingIsActuals = (billing: Billing) =>
    billing && (billingIsActualsV1(billing) || billingIsActualsV2(billing));
export const billingIsMonthly = (billing: Billing) => billing && billing.id === BILLING_MONTHLY;
export const billingIsAnnualized = (billing: Billing) =>
    billing && (billing.id === BILLING_ANNUALIZED || billing.id === BILLING_ANNUALIZED_MID_MONTH);
export const billingSupportsMultipleFrequencies = (billing: Billing) =>
    !billingIsActuals(billing) && !billingIsMonthly(billing);

type Booking = ProductBooking | SessionBooking;

export interface BookingLine {
    title: string;
    quantity: number;
    weeklyPrice: number;
    monthlyPrice: number;
}

export interface BookingLineGroup {
    lineId: string;
    color: string;
    title: string;
    volume: number;
    quantity: number;
    discountable: boolean;
    weeklyPrice: number;
    monthlyPrice: number;
    pricing: any;
    bookings: List<BookingLine>;
    hideSingleLinePrice: boolean;
}

export interface IPublicFunding {
    readonly amount: number | null;
    readonly period: string;
}

export class PublicFunding extends Record<IPublicFunding>({
    amount: 0,
    period: FREQUENCY.MONTHLY,
}) {}

export const getPublicFunding = (amount: number | null, period: string) => {
    return new PublicFunding({
        amount,
        period,
    });
};

const getWeeksOfCareAndMonthsToInvoice = (billing: Billing) => {
    let weeksOfCare = 52;
    let monthsToInvoice = 11;
    if (billing) {
        weeksOfCare = billing.weeksOfCare;
        monthsToInvoice = billing.invoices;
    }

    return {
        weeksOfCare,
        monthsToInvoice,
    };
};

export const convertToWeekly = (price: number, pricing: string, billing: Billing) => {
    if (pricing === PricingType.MONTHLY_PRICING) {
        const {weeksOfCare, monthsToInvoice} = getWeeksOfCareAndMonthsToInvoice(billing);
        return (price * monthsToInvoice) / weeksOfCare;
    } else {
        return price;
    }
};

export const getWeeklyPrice = (sessionPricingGroup: any, billing: Billing) => {
    return convertToWeekly(sessionPricingGroup.price, sessionPricingGroup.pricing, billing);
};

const convertToMonthly = (price: number, pricing: string, billing: Billing) => {
    if (pricing === PricingType.MONTHLY_PRICING) {
        return price;
    } else {
        const {weeksOfCare, monthsToInvoice} = getWeeksOfCareAndMonthsToInvoice(billing);
        return (price * weeksOfCare) / monthsToInvoice;
    }
};

const getMonthlyPrice = (sessionPricingGroup: any, billing: Billing) => {
    return convertToMonthly(sessionPricingGroup.price, sessionPricingGroup.pricing, billing);
};

/**
 * Calculate the monthly total price for the plan based on the configuration of the public
 * funding, discount, adjustment and the monthly cost calculation.
 */
const getModifierWeekly = (modifier: any, billing: Billing) => {
    const surchargeMultiplier = modifier.isSurcharge ? -1 : 1;
    const value = modifier.amount * surchargeMultiplier;

    switch (modifier.period) {
        case FREQUENCY.WEEKLY:
            return value;
        case FREQUENCY.MONTHLY: {
            const {weeksOfCare, monthsToInvoice} = getWeeksOfCareAndMonthsToInvoice(billing);
            return (value * monthsToInvoice) / weeksOfCare;
        }
        case FREQUENCY.YEARLY: {
            const {weeksOfCare} = getWeeksOfCareAndMonthsToInvoice(billing);
            return value / weeksOfCare;
        }
        case FREQUENCY.DAILY:
            return modifier.amount;
        default:
            throw new Error(`Unknown frequency ${modifier.period}`);
    }
};

const getModifierMonthly = (modifier: any, billing: Billing) => {
    switch (modifier.period) {
        case FREQUENCY.WEEKLY: {
            const {weeksOfCare, monthsToInvoice} = getWeeksOfCareAndMonthsToInvoice(billing);
            return (modifier.amount * weeksOfCare) / monthsToInvoice;
        }
        case FREQUENCY.MONTHLY:
            return modifier.amount;
        case FREQUENCY.YEARLY:
            return modifier.amount / 12;
        case FREQUENCY.DAILY:
            return modifier.amount;
        default:
            throw new Error(`Unknown frequency ${modifier.period}`);
    }
};

export const sessionsToMap = (sessions: List<any>): Map<string, any> =>
    Map(sessions.map(session => [session.version, session]));

// Get a map from session identifier (id, version or otherwise) to price
const localSessionPrices = (
    sessionsMap: Map<string, any>,
    bookedSessions: Map<string, number>,
    pricingGroupId: string,
    sessionToId: (session: any) => string,
) => {
    // Get a map from volumeDiscountGroupId to list of session identifiers
    const volumeDiscountGroups = sessionsMap
        .valueSeq()
        .filter(session => session.volumeDiscountGroupId)
        .groupBy(session => session.volumeDiscountGroupId)
        // The sessionsMap can hold multiple versions of the same session. This ensures unique session ids
        .map(sessions => sessions.map(sessionToId).toSet().toList());

    // Get a count for each volume group of how many sessions are booked
    const countsInGroup = volumeDiscountGroups.map(sessionIds =>
        sessionIds.reduce((count, sessionId) => {
            const currentCount = bookedSessions.get(sessionId);
            return currentCount ? count + currentCount : count;
        }, 0),
    );

    // Return the map of session identifier to price
    return sessionsMap.map(session => {
        // Get the prices applicable for the current pricingGroupId
        const prices = List(session.pricing)
            .filter((ag: any) => ag.pricingGroupId === pricingGroupId)
            .sortBy((pricing: any) => pricing.volume);

        // If the sessions volumeDiscountGroupId is in the volume counts
        // get the price that matches the number of booked sessions in that group
        if (countsInGroup.has(session.volumeDiscountGroupId)) {
            const count = countsInGroup.get(session.volumeDiscountGroupId) as number;
            return prices.reduce((last: any, pricing: any) =>
                pricing.volume <= count && pricing.volume > last.volume ? pricing : last,
            );
        }

        // otherwise get the price for volume === 1
        // since that is the applicable price for sessions not in volume groups
        return prices.find((pricing: any) => pricing.volume === 1);
    });
};

export const sessionPricesImmutable = (
    sessionsMap: Map<string, any>,
    bookedSessions: Map<string, number>,
    pricingGroupId: string,
) => {
    return localSessionPrices(sessionsMap, bookedSessions, pricingGroupId, session => session.sessionId);
};

export const getVolumeGroupedSessionsFromMap = (sessionsMap: Map<string, any>) =>
    sessionsMap
        .valueSeq()
        .filter(session => session.volumeDiscountGroupId)
        .groupBy(session => session.volumeDiscountGroupId)
        .map(value => value.toList());

const getBookingGroupSummary = (bookings: List<any>) => {
    return bookings.reduce(
        (obj, child) => ({
            quantity: obj.quantity + child.quantity,
            weeklyPrice: obj.weeklyPrice + child.weeklyPrice,
            monthlyPrice: obj.monthlyPrice + child.monthlyPrice,
        }),
        {
            quantity: 0,
            weeklyPrice: 0,
            monthlyPrice: 0,
        },
    );
};

const getBookingFundedTitle = (booking: any) => {
    if (!booking.fundedMinutes) {
        return '';
    }

    const hours = Math.floor(booking.fundedMinutes / 60);
    const minutes = booking.fundedMinutes - hours * 60;

    if (hours && minutes) {
        return ` (${hours} ${i18next.t('time.hoursAbbr')} ${minutes} ${i18next.t('time.minutesAbbr')} ${i18next.t(
            'plans.managePlan.pricing.fundedSmall',
        )})`;
    } else if (hours) {
        return ` (${hours} ${i18next.t('time.hoursAbbr')} ${i18next.t('plans.managePlan.pricing.fundedSmall')})`;
    } else {
        return ` (${minutes} ${i18next.t('time.minutesAbbr')} ${i18next.t('plans.managePlan.pricing.fundedSmall')})`;
    }
};

const getBookingTitle = (booking: Booking) => {
    const translatedDay = getWeekdayTitle(booking.day);
    const fundedTitle = getBookingFundedTitle(booking);

    if (booking instanceof SessionBooking && booking.start && booking.end) {
        return `${translatedDay} ${booking.start.format('LT')}-${booking.end.format('LT')}${fundedTitle}`;
    }
    return `${translatedDay}${fundedTitle}`;
};

const createBookingLine = (
    booking: Booking,
    quantity: number,
    weeklyPrice: number,
    monthlyPrice: number,
    getBookingPreTitle?: (booking: Booking) => string,
): BookingLine => {
    const preTitle = getBookingPreTitle ? getBookingPreTitle(booking) : null;
    return {
        title: `${preTitle ? `${preTitle} ` : ''}${getBookingTitle(booking)}`,
        quantity,
        weeklyPrice,
        monthlyPrice,
    };
};

const findPriceByPricingGroupId = (pricingGroupId: string, monthlyPrices: MonthlyPrice[]): MonthlyPrice | undefined =>
    monthlyPrices.find(monthlyPrice => monthlyPrice.pricingGroupId === pricingGroupId);

const getMonthlySessionPriceFromBooking = (pricingGroupId: string, booking: SessionBooking): number => {
    const monthlySessionPriceObj = findPriceByPricingGroupId(pricingGroupId, booking.monthlyPrices);
    return monthlySessionPriceObj ? monthlySessionPriceObj.price : 0;
};

const isProductBooking = (booking: Booking): booking is ProductBooking => {
    return (booking as ProductBooking).productId !== undefined;
};

export const getBookingLinesFromGroup = (
    group: List<Booking>,
    bookingToAmount: (booking: any) => number,
    pricing: any,
    billing: Billing,
    getBookingPreTitle?: (booking: any) => string,
): List<BookingLine> => {
    const weeklyPrice = getWeeklyPrice(pricing, billing);

    const bookingLines = group.map(booking => {
        const quantity = bookingToAmount(booking);

        const weeklyTotal = roundToDecimals(quantity * weeklyPrice, 2);

        const monthlyTotal = isProductBooking(booking)
            ? roundToDecimals(getMonthlyPrice(pricing, billing) * quantity, 2)
            : // For sessions, the monthly price is calculated on the backend. Thus, we don't need
              // to multiply it by the quantity as doing this leads to a number that's way too high.
              // We still need to do the multiplication for products, though. The 100% correct solution
              // would be to always use the monthly prices calculated by the backend, regardless of
              // the booking type.
              getMonthlySessionPriceFromBooking(pricing.pricingGroupId, booking);

        return createBookingLine(booking, quantity, weeklyTotal, monthlyTotal, getBookingPreTitle);
    });

    return bookingLines;
};

export const mapProductBookingGroup = (
    group: List<ProductBooking>,
    product: any,
    pricing: any,
    billing: Billing,
): BookingLineGroup => {
    const bookingToAmount = (booking: ProductBooking) => booking.amount;

    const bookings = getBookingLinesFromGroup(group, bookingToAmount, pricing, billing);

    const groupSummary = getBookingGroupSummary(bookings);

    return {
        lineId: product.productId,
        discountable: product.discountable,
        title: product.title,
        volume: pricing.volume,
        pricing: pricing.pricing,
        bookings,
        ...groupSummary,
        color: `${product.color}`,
        hideSingleLinePrice: false,
    };
};

export const getQuantity = (booking: SessionBooking, session: any, pricing: any) => {
    // The first check will never be true, as it has been checked for before this code is ever
    // executed
    if (session.sessionType === Session.SessionType.PACKAGE_SESSION || pricing.pricing !== PricingType.HOURLY) {
        return 1;
    }

    if (session.flexibleHours) {
        if (booking.end && booking.start) {
            return booking.end.diff(booking.start, 'hours', true);
        } else {
            return 1;
        }
    } else {
        const availability = session.availability.find((av: any) => av.day === booking.day);

        // Availability can be null if session has been changed and is no longer available on this day.
        return availability ? availability.end.diff(availability.start, 'hours', true) : 0;
    }
};

// For volume pricing if we need to calculate the monthly (for weekly sessions)
// or weekly (for monthly sessions) we need to do the conversion on a per booking basis
// to do this we use need to split the total price out to each booking
// fx for 3 sessions costing 31 in total the prices will be [10.34, 10.33, 10.33]
// and then convert those prices individually, round them and summarize them
// to do this, we use getBookingPrices to find the minimum price per booking
// (in this case 10.33) and the remainder (1)
// the remainder is then spread out to the bookings using shareRestToBookings
// and then converted in the end and rounded individually before summarized in getVolumePricingPrice

const shareRestToBookings = (bookings: List<number>, remainder: number): List<number> => {
    const mappedBookings = bookings.reduce(
        (aggr, current) => ({
            list: aggr.list.push(current + (aggr.remainder > 0 ? 1 : 0)),
            remainder: aggr.remainder - 1,
        }),
        {
            list: List(),
            remainder,
        },
    );

    if (mappedBookings.remainder > 0) {
        return shareRestToBookings(mappedBookings.list, mappedBookings.remainder);
    }

    return mappedBookings.list;
};

const getBookingPrices = (bookings: List<BookingLine>, price: number) => {
    const priceMultiplied = Math.floor(price * 100);
    const bookingPrice = Math.floor(priceMultiplied / bookings.size);
    const remainder = priceMultiplied - Math.floor(bookingPrice * bookings.size);
    const mappedBookingPrices = bookings.map(() => bookingPrice);
    return shareRestToBookings(mappedBookingPrices, remainder).map(a => a / 100);
};

const sumList = (list: List<number>) => list.reduce((aggr, current) => aggr + current, 0);

const getVolumePricingPrice = (bookings: List<BookingLine>, pricing: any, billing: Billing) => {
    if (pricing.pricing === PricingType.MONTHLY_PRICING) {
        const monthlyPrice = getMonthlyPrice(pricing, billing);
        const weeklyPrices = getBookingPrices(bookings, monthlyPrice).map(
            price => roundToDecimals(convertToWeekly(price, pricing.pricing, billing), 2),
            2,
        );
        return {
            weeklyPrice: sumList(weeklyPrices),
            monthlyPrice,
        };
    } else {
        const weeklyPrice = getWeeklyPrice(pricing, billing);
        const monthlyPrices = getBookingPrices(bookings, weeklyPrice).map(price =>
            roundToDecimals(convertToMonthly(price, pricing.pricing, billing), 2),
        );
        return {
            weeklyPrice,
            monthlyPrice: sumList(monthlyPrices),
        };
    }
};

export const mapSessionBookingGroup = (
    group: List<any>,
    sessions: List<any>,
    pricing: any,
    billing: Billing,
    planPart: PlanPart,
): BookingLineGroup => {
    const isPackage = sessions.size === 1 && sessions.first().sessionType === Session.SessionType.PACKAGE_SESSION;

    const sessionData = {
        lineId: sessions.first().sessionId,
        discountable: true,
        volume: pricing.volume,
        pricing: pricing.pricing,
        color: isPackage ? 'package' : `${sessions.first().color}`,
    };

    if (isPackage) {
        return {
            ...sessionData,
            title: sessions.map(s => s.title).join(', '),
            quantity: 1,
            weeklyPrice: getWeeklyPrice(pricing, billing),
            monthlyPrice: planPart.sessionBookings
                .filter(sessionBooking => sessionBooking.sessionVersion === sessions.first().version)
                .reduce((monthlyPrice, sessionBooking) => {
                    const monthlyPriceObj = findPriceByPricingGroupId(
                        pricing.pricingGroupId,
                        sessionBooking.monthlyPrices,
                    );

                    if (!monthlyPriceObj) {
                        return monthlyPrice;
                    }

                    return roundToDecimals(monthlyPrice + monthlyPriceObj.price, 2);
                }, 0),
            bookings: List(),
            hideSingleLinePrice: false,
        };
    }

    const bookingToAmount = (booking: SessionBooking) => getQuantity(booking, sessions.first(), pricing);

    const getBookingPreTitle = (booking: SessionBooking) =>
        sessions.first().volumeDiscountGroupId
            ? sessions.find(session => session.sessionId === booking.sessionId).title
            : null;

    const bookings = getBookingLinesFromGroup(group, bookingToAmount, pricing, billing, getBookingPreTitle);

    const groupSummary = getBookingGroupSummary(bookings);

    const isVolumeSession = group.size > 1 && sessions.first().sessionType === Session.SessionType.VOLUME_SESSION;

    const sessionGroupData = {
        ...sessionData,
        title: sessions
            .filter(session => group.some(booking => booking.sessionId === session.sessionId))
            .map(s => s.title)
            .toSeq()
            .join(', '),
        hideSingleLinePrice: isVolumeSession,
        bookings,
        ...groupSummary,
    };

    if (isVolumeSession) {
        return {
            ...sessionGroupData,
            ...getVolumePricingPrice(bookings, pricing, billing),
        };
    }

    return sessionGroupData;
};

export const getWeeklySubtotalFromLines = (lines: List<any>) => lines.reduce((aggr, e) => aggr + e.weeklyPrice, 0);

const subtractPublicFunding = (frequency: string, value: number, publicFunding?: PublicFunding) => {
    return publicFunding && publicFunding.period === frequency && publicFunding.amount !== null
        ? value - publicFunding.amount
        : value;
};

const weeklyTotalWithoutDiscount = (weeklySubtotal: number, publicFunding?: PublicFunding) => {
    return Math.max(subtractPublicFunding(FREQUENCY.WEEKLY, weeklySubtotal, publicFunding), 0);
};

const monthlySubtractPublicFunding = (monthly: number, billing: Billing, publicFunding?: PublicFunding) => {
    if (!publicFunding) {
        return monthly;
    }

    return Math.max(monthly - getModifierMonthly(publicFunding, billing), 0);
};

const addSurchargeOrDiscount = (discount: Discount, discountableAmount: number) => {
    const {isPercent, amount} = discount;

    // If the discount is a percent discount, then this function assumes
    // that `amount` is between 0 and 1 (representing an actual percentage).
    // If the discount is by amount, we must ensure that we don't subtract
    // more than `discountableAmount`
    const newValue = isPercent
        ? discountableAmount - discountableAmount * amount
        : Math.max(discountableAmount - amount, 0);

    return roundToDecimals(newValue, 2);
};

export const weeklyTotal = (
    weeklyPublicFundedAmount: number,
    weeklyNonPublicFundedAmount: number,
    weeklyNonPublicOrDiscountedFundedAmount: number,
    discounts: List<Discount>,
    publicFunding?: PublicFunding,
) => {
    const withoutDiscount =
        weeklyTotalWithoutDiscount(weeklyPublicFundedAmount, publicFunding) + weeklyNonPublicFundedAmount;

    const weeklyTotal =
        discounts.reduce((reduction, discount) => {
            return discount.period === FREQUENCY.WEEKLY ? addSurchargeOrDiscount(discount, reduction) : reduction;
        }, withoutDiscount) + weeklyNonPublicOrDiscountedFundedAmount;

    return roundToDecimals(weeklyTotal, 2);
};

/**
 * Depending on the configuration of the discount either return the
 * discounted amount as is, or calculate it based on the weekly subtotal and the
 * monthly cost calculation.
 */
export const addDiscountWeekly = (discount: Discount, billing: Billing, amount: number) => {
    if (discount.isPercent) {
        return addSurchargeOrDiscount(discount, amount);
    }
    const modifier = discount.merge({
        period: FREQUENCY.WEEKLY,
        amount: getModifierWeekly(discount, billing),
    });
    return addSurchargeOrDiscount(modifier, amount);
};

const monthlySubtotal = (weeklyAmount: number, billing: Billing) => {
    const multiplyWeeks = (value: number) => {
        const {weeksOfCare, monthsToInvoice} = getWeeksOfCareAndMonthsToInvoice(billing);
        return (value * weeksOfCare) / monthsToInvoice;
    };

    return multiplyWeeks(weeklyAmount);
};

const monthlyAddSurchargeOrDiscount = (amount: number, discount: Discount, billing: Billing) => {
    let monthlyAmount = amount;

    monthlyAmount = discount.isPercent
        ? monthlyAmount - roundToDecimals((monthlyAmount * discount.amount) / 100, 2)
        : monthlyAmount - roundToDecimals(getModifierMonthly(discount, billing), 2);

    return monthlyAmount;
};

const monthlyTotal = (
    monthlyPublicFundedSubTotal: number,
    monthlyNonPublicFundedSubTotal: number,
    discounts: List<Discount>,
    billing: Billing,
    publicFunding?: PublicFunding,
) => {
    let monthlyAmount =
        monthlySubtractPublicFunding(monthlyPublicFundedSubTotal, billing, publicFunding) +
        monthlyNonPublicFundedSubTotal;

    monthlyAmount = discounts.reduce((reduction, discount) => {
        return monthlyAddSurchargeOrDiscount(reduction, discount, billing);
    }, monthlyAmount);

    return monthlyAmount;
};

/**
 * Depending on the configuration of the discount either return the
 * discounted percentage as is, or calculate it based on the weekly subtotal and the
 * monthly cost calculation.
 */
export const discountPercentage = (
    discount: Discount,
    billing: Billing,
    weeklySubtotal: number,
    publicFunding?: PublicFunding,
) => {
    if (discount.isPercent) {
        return discount.amount;
    }

    if (discount.amount === 0 || weeklySubtotal === 0) {
        return 0;
    }

    if (discount.period === FREQUENCY.WEEKLY) {
        const weeklyWithoutDiscount = weeklyTotalWithoutDiscount(weeklySubtotal, publicFunding);

        if (weeklyWithoutDiscount === 0) {
            return 0;
        }
        return discount.amount / weeklyWithoutDiscount;
    }

    const monthlySub = monthlySubtotal(weeklySubtotal, billing);
    let monthlyWithoutDiscount = monthlySub;
    if (
        (publicFunding && publicFunding.period === discount.period) ||
        discount.period === FREQUENCY.MONTHLY ||
        discount.period === FREQUENCY.YEARLY
    ) {
        monthlyWithoutDiscount = monthlySubtractPublicFunding(monthlySub, billing, publicFunding);
    }
    const monthly = monthlyTotal(monthlySub, 0, List([discount]), billing, publicFunding);

    return 1 - monthly / monthlyWithoutDiscount;
};
